import { css } from '@emotion/react';
import {
  type FC, useState,
} from 'react';
import { useDispatch } from 'react-redux';
import { AlertComponent } from '~/_shared/baseComponents/alert';
import { CheckboxComponent } from '~/_shared/baseComponents/checkbox';
import { OverlayLoaderComponent } from '~/_shared/components/overlay/overlayLoader.component';
import { OverlayWrapperComponent } from '~/_shared/components/overlay/overlayWrapper.component';
import { useTranslation } from '~/_shared/utils/hooks';
import { ExportReportType } from '~/exportReport/exportReportType';
import type { ReportTemplate } from '~/exportReport/template/reportTemplate';
import { ExportReportTemplateLoaderComponent } from '~/exportReport/templateLoader/exportReportTemplateLoader.component';
import { ExportReportTemplateRendererComponent } from '~/exportReport/templateRenderer/exportReportTemplateRenderer.component';
import { useExportReportData } from '~/exportReport/useExportReportData';
import { useExportReportParams } from '~/exportReport/useExportReportParams';
import { useLatLngSpreadsheetData } from '~/map/map/useSpreadsheetData.hook';
import { MapExportReportPreviewHeaderComponent } from '~/map/page/exportReportPreview/mapExportReportPreviewHeader.component';
import { createReportCreateRequest } from '~/store/frontendState/createReport/createReport.actionCreators';
import { exportReportDisable } from '~/store/mapSettings/toolsState/exportReport/exportReportToolState.actionCreators';
import type { ExportReportLocationParams } from '~/store/mapSettings/toolsState/exportReport/exportReportToolState.state';
import { useMapIdSelector } from '~/store/selectors/useMapIdSelector';

const contentStyle = css({
  padding: 20,
  maxWidth: 1000,
  margin: '0 auto',
});

const googleMapStyle = css({
  width: '50%',
  aspectRatio: '386 / 234',
});

const settingsWrapperStyle = css({
  width: 1000,
  margin: '20px auto 0',
});

type MapExportReportPreviewLocationProps = Readonly<{
  params: ExportReportLocationParams;
  template: ReportTemplate;
}>;

export const MapExportReportPreviewLocationComponent: FC<MapExportReportPreviewLocationProps> = (props) => {
  const [isPreviewMode, setIsPreviewMode] = useState(true);
  const dispatch = useDispatch();
  const mapId = useMapIdSelector();
  const [t] = useTranslation();

  const spreadsheetData = useLatLngSpreadsheetData();
  const rowData = spreadsheetData.getRow(props.params.data.markerId);
  const { tableColumns } = useExportReportParams();
  const { isLoading, getDemographicValue, getDemographicLabel, isError } = useExportReportData({
    isPreviewMode,
    template: props.template,
    center: rowData ?? null,
  });

  const closePdfSummary = () => {
    dispatch(exportReportDisable());
  };

  const onPrintClick = () => {
    if (!mapId) {
      return;
    }

    dispatch(createReportCreateRequest(mapId));
  };

  if (!rowData) {
    return null;
  }

  return (
    <div>
      <MapExportReportPreviewHeaderComponent onCloseClick={closePdfSummary} onPrintClick={onPrintClick} />

      {isError && (
        <AlertComponent type="danger">{t('Error Exporting a Report')}</AlertComponent>
      )}

      <div css={settingsWrapperStyle}>
        <ExportReportTemplateLoaderComponent />

        <CheckboxComponent
          checkedSetter={() => setIsPreviewMode(usePreview => !usePreview)}
          isChecked={isPreviewMode}
          text="Template Preview (no data fetch)"
        />
      </div>

      <div css={contentStyle}>
        <OverlayWrapperComponent>
          <ExportReportTemplateRendererComponent
            template={props.template}
            tableColumns={tableColumns}
            summaryProps={{
              latLng: {
                lat: rowData.lat,
                lng: rowData.lng,
              },
              heading: props.params.data.summary.heading,
              semiHeading: props.params.data.summary.semiHeading,
              description: props.params.data.summary.description,
            }}
            mapProps={{
              center: rowData,
              css: googleMapStyle,
              type: ExportReportType.Location,
            }}
            getDemographicLabel={getDemographicLabel}
            getDemographicValue={getDemographicValue}
            isPreviewMode={isPreviewMode}
          />

          {isLoading && <OverlayLoaderComponent />}
        </OverlayWrapperComponent>
      </div>
    </div>
  );
};
